import { useCallback } from 'react'
import { useQueryParams } from 'use-query-params'
import { useHistory, useLocation } from 'react-router'
import { retailerLocationSelectQueryParamEncoder } from 'utils/query-params/retailerLocationSelect.utils'
import { dateSelectRangeQueryParamEncoder } from 'utils/query-params/dateSelectRange.utils'
import { type DateSelectRangeValueType } from 'legacy/dash-components/date-select-range/utils/dateSelectRange.types'
import { type RetailerLocationSelectValueType } from 'legacy/dash-components/retailer-location-select/utils/options.types'
import { type OrderType, type OrderSource } from 'legacy/sections/orders/utils/orders.types'
import { makeGenericStringQueryParamEncoder } from 'utils/query-params/string.utils'
import { useRetailerId } from 'utils/routing/params.hooks'
import { type DashRouteComponentProps } from 'legacy/dash-components/dash-route/utils/dashRoute.types'
import { type RetailerScopedRouteParams } from 'legacy/common/types'
import { url } from 'utils/parsing/url'

export interface UsePerformanceMetricsQueryParamsProps
  extends Pick<DashRouteComponentProps<RetailerScopedRouteParams>['match'], 'params' | 'path'> {}

export const usePerformanceMetricsQueryParams = ({
  params,
  path,
}: UsePerformanceMetricsQueryParamsProps) => {
  const [queryValue, setQueryValue] = useQueryParams({
    orderType: makeGenericStringQueryParamEncoder<OrderType>(),
    orderSource: makeGenericStringQueryParamEncoder<OrderSource>(),
    dateSelectRangeValue: dateSelectRangeQueryParamEncoder,
    retailerLocationSelectValue: retailerLocationSelectQueryParamEncoder,
  })

  const setDateSelectRangeValue = useCallback(
    (nextDateSelectRangeValue: DateSelectRangeValueType) => {
      setQueryValue({
        ...queryValue,
        dateSelectRangeValue: nextDateSelectRangeValue,
      })
    },
    [queryValue, setQueryValue]
  )

  const setRetailerLocationSelectValue = useCallback(
    (nextRetailerLocationSelectValue: RetailerLocationSelectValueType) => {
      setQueryValue({
        ...queryValue,
        retailerLocationSelectValue: nextRetailerLocationSelectValue,
      })
    },
    [queryValue, setQueryValue]
  )

  const setOrderType = useCallback(
    (nextOrderType: OrderType) => {
      setQueryValue({
        ...queryValue,
        orderType: nextOrderType,
      })
    },
    [queryValue, setQueryValue]
  )

  const setOrderSource = useCallback(
    (nextOrderSource: OrderSource) => {
      setQueryValue({
        ...queryValue,
        orderSource: nextOrderSource,
      })
    },
    [queryValue, setQueryValue]
  )

  // Not currently query params, but should be turned into query params in the future
  const retailerId = useRetailerId()
  const history = useHistory()
  const { search } = useLocation()

  // Replaces the warehouse_id in the current url with the selected one
  // Note that this will switch the current retailer for the _whole_ app
  // Ideally we can eventually update this to just be another query param
  const setRetailerId = useCallback(
    (nextRetailerId: string) => {
      const pathname = url(path, { ...params, warehouse_id: nextRetailerId })
      history.push({ pathname, search })
    },
    [history, params, path, search]
  )

  const { orderType, orderSource, dateSelectRangeValue, retailerLocationSelectValue } = queryValue

  return {
    retailerId,
    setRetailerId,
    orderType,
    setOrderType,
    orderSource,
    setOrderSource,
    dateSelectRangeValue,
    setDateSelectRangeValue,
    retailerLocationSelectValue,
    setRetailerLocationSelectValue,
  }
}

export type PerformanceMetricsQueryParamsBehaviour = ReturnType<
  typeof usePerformanceMetricsQueryParams
>
